import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import MultiSelectAsyncV2 from '../MultiSelectAsyncV2';
import MultiSelectV2 from '../MultiSelectV2';

const DEFAULT_MIN_CHARS = 2;

const toIdList = (value) => {
  if (!Array.isArray(value) || value.length === 0) return [];
  if (typeof value[0] === 'object') return value.map((item) => item.value);
  return value;
};

const fallbackOptions = (ids) =>
  ids.map((id) => ({ value: id, label: String(id) }));

/**
 * Multi-select de faceta para filtros.
 * `sync`: carrega a lista uma vez (`loadAll` ou `options`) e filtra no cliente.
 * `async`: busca no servidor conforme a digitação (`loadOptions`).
 */
const FilterFacetSelectV2 = ({
  mode,
  label,
  value,
  onSelect,
  disabled,
  loadAll,
  options,
  loadOptions,
  loadSelected,
  minChars,
  placeholder,
  groupBy,
  reloadKey,
  limitTags,
}) => {
  const [fetchedOptions, setFetchedOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const loadAllRef = useRef(loadAll);
  const loadSelectedRef = useRef(loadSelected);
  loadAllRef.current = loadAll;
  loadSelectedRef.current = loadSelected;

  const providedOptions = Array.isArray(options) ? options : null;
  const isSync = mode !== 'async';

  useEffect(() => {
    if (!isSync || providedOptions) return undefined;
    if (typeof loadAllRef.current !== 'function') {
      setFetchedOptions([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const next = await loadAllRef.current();
        if (!cancelled) setFetchedOptions(Array.isArray(next) ? next : []);
      } catch (e) {
        if (!cancelled) setFetchedOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSync, providedOptions, reloadKey]);

  useEffect(() => {
    if (isSync) return undefined;
    let cancelled = false;
    const ids = toIdList(value);

    if (!ids.length) {
      setSelectedObjects([]);
      return undefined;
    }

    if (!loadSelectedRef.current) {
      setSelectedObjects(fallbackOptions(ids));
      return undefined;
    }

    setLoading(true);
    (async () => {
      try {
        const next = await loadSelectedRef.current(ids);
        if (!cancelled) {
          setSelectedObjects(Array.isArray(next) && next.length ? next : fallbackOptions(ids));
        }
      } catch (e) {
        if (!cancelled) setSelectedObjects(fallbackOptions(ids));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isSync, value]);

  const syncOptions = providedOptions || fetchedOptions;
  const syncValue = useMemo(() => {
    const ids = toIdList(value);
    return ids.map((id) => {
      const found = syncOptions.find((option) => option.value === id);
      return found || { value: id, label: String(id) };
    });
  }, [value, syncOptions]);

  const handleSyncChange = (selected) => {
    const next = selected && selected.length ? selected.map((item) => item.value) : null;
    onSelect(next);
  };

  const handleAsyncLoad = useCallback(async (inputValue) => {
    const query = inputValue || '';
    if (query.length < minChars) return [];
    if (typeof loadOptions !== 'function') return [];
    try {
      const next = await loadOptions(query);
      return Array.isArray(next) ? next : [];
    } catch (e) {
      return [];
    }
  }, [loadOptions, minChars]);

  const handleAsyncChange = (selected) => {
    const next = selected || [];
    setSelectedObjects(next);
    onSelect(next.length ? next.map((item) => item.value) : null);
  };

  if (!isSync) {
    return (
      <MultiSelectAsyncV2
        label={label}
        disabled={disabled || loading}
        loadOptions={handleAsyncLoad}
        value={selectedObjects}
        placeholder={
          loading
            ? 'Carregando...'
            : (placeholder || `Digite ${minChars}+ caracteres para buscar`)
        }
        onChange={handleAsyncChange}
      />
    );
  }

  return (
    <MultiSelectV2
      label={label}
      disabled={disabled || loading}
      options={syncOptions}
      groupBy={groupBy}
      defaultValue={syncValue}
      limitTags={limitTags}
      onChange={handleSyncChange}
    />
  );
};

FilterFacetSelectV2.propTypes = {
  mode: PropTypes.oneOf(['sync', 'async']),
  label: PropTypes.string.isRequired,
  value: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loadAll: PropTypes.func,
  options: PropTypes.array,
  loadOptions: PropTypes.func,
  loadSelected: PropTypes.func,
  minChars: PropTypes.number,
  placeholder: PropTypes.string,
  groupBy: PropTypes.func,
  reloadKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  limitTags: PropTypes.number,
};

FilterFacetSelectV2.defaultProps = {
  mode: 'sync',
  value: null,
  disabled: false,
  loadAll: undefined,
  options: undefined,
  loadOptions: undefined,
  loadSelected: undefined,
  minChars: DEFAULT_MIN_CHARS,
  placeholder: undefined,
  groupBy: undefined,
  reloadKey: undefined,
  limitTags: 2,
};

export default FilterFacetSelectV2;
